// src/services/analyticsService.js
import { getUserAddDataFromLocalStorage } from '@/utils/userUtils';
import { initializeAnalytics, logEvent, setUserId, setUserProperties } from 'firebase/analytics';
import { newFirebaseApp } from '@/firebase/firebaseInit';

const PRIVATE_KEY_PARTS = [
    'email',
    'username',
    'displayname',
    'nickname',
    'firstname',
    'givenname',
    'forename',
    'lastname',
    'surname',
    'familyname',
    'fullname'
];

class AnalyticsService {
    constructor() {
        this.isInitialized = false;
        this.gtag = null;
        this.analytics = null;
        this.currentUser = null;
    }

    /**
     * Initialize the analytics service
     */
    init() {
        try {
            // Initialize Firebase Analytics
            this.analytics = initializeAnalytics(newFirebaseApp, {
                config: { send_page_view: false }
            });
            this.isInitialized = true;
            console.log('Firebase Analytics service initialized');
            
            // Also initialize gtag for backward compatibility
            if (typeof window !== 'undefined' && window.gtag) {
                this.gtag = window.gtag;
                console.log('Google Analytics (gtag) also available for backward compatibility');
            }
            
            // Set up user properties if user is already logged in
            this.updateUserProperties();
        } catch (error) {
            console.error('Error initializing Firebase Analytics:', error);
            
            // Fallback to gtag if Firebase Analytics fails
            if (typeof window !== 'undefined' && window.gtag) {
                this.gtag = window.gtag;
                this.isInitialized = true;
                console.log('Fallback to Google Analytics (gtag)');
                this.updateUserProperties();
            } else {
                console.warn('Neither Firebase Analytics nor Google Analytics (gtag) available');
            }
        }
    }

    normalizePrivateKey(key) {
        return String(key).toLowerCase().replace(/[^a-z0-9]/g, '');
    }

    isPrivateKey(key) {
        const normalizedKey = this.normalizePrivateKey(key);
        return PRIVATE_KEY_PARTS.some((part) => normalizedKey.includes(part));
    }

    privateValues() {
        const userData = this.currentUser || getUserAddDataFromLocalStorage() || {};
        return [
            userData.email,
            userData.username,
            userData.userName,
            userData.displayName,
            userData.firstName,
            userData.lastName
        ]
            .filter((value) => typeof value === 'string' && value.trim())
            .map((value) => value.trim());
    }

    sanitizePrivateText(value) {
        if (typeof value !== 'string') return value;

        let sanitized = value.replace(/[\w.+-]+@[\w-]+\.[\w.-]+/g, '[redacted]');
        for (const privateValue of this.privateValues()) {
            const escapedValue = privateValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            sanitized = sanitized.replace(new RegExp(escapedValue, 'gi'), '[redacted]');
        }
        return sanitized;
    }

    sanitizeAnalyticsValue(value = {}) {
        if (Array.isArray(value)) {
            return value.map((item) => this.sanitizeAnalyticsValue(item));
        }
        if (value && typeof value === 'object') {
            return Object.fromEntries(
                Object.entries(value)
                    .filter(([key]) => !this.isPrivateKey(key))
                    .map(([key, item]) => [key, this.sanitizeAnalyticsValue(item)]),
            );
        }
        return this.sanitizePrivateText(value);
    }

    /**
     * Rebuild the route's real path, keeping non-private param values but
     * redacting any param whose name looks private (email, name, etc.)
     */
    buildSafePath(to) {
        const template =
            to?.matched?.at(-1)?.path || (typeof to?.name === 'string' ? `/${to.name}` : to?.path || '/');

        const path = template
            .split('/')
            .map((segment) => {
                const match = segment.match(/^:(\w+)/);
                if (!match) return segment;

                const paramName = match[1];
                if (this.isPrivateKey(paramName)) return '[redacted]';

                const value = to?.params?.[paramName];
                return Array.isArray(value)
                    ? value.map(encodeURIComponent).join('/')
                    : encodeURIComponent(value ?? '');
            })
            .join('/');

        const query = to?.query ? new URLSearchParams(to.query).toString() : '';
        return query ? `${path}?${query}` : path;
    }

    sanitizePageLocation(pagePath) {
        const rawPath = typeof pagePath === 'string' && pagePath ? pagePath : '/';
        // Build a full URL for page_location since send_page_view:false disabled Firebase's own (unsanitized) auto-collection of it
        const url = new URL(rawPath, window.location.origin);
        const safeQuery = new URLSearchParams();

        for (const [key, value] of url.searchParams.entries()) {
            if (this.isPrivateKey(key)) continue;
            safeQuery.set(key, this.sanitizePrivateText(value));
        }

        url.search = safeQuery.toString();
        url.hash = '';
        url.pathname = this.sanitizePrivateText(url.pathname);
        return url.toString();
    }

    /**
     * Update user properties from localStorage
     */
    updateUserProperties() {
        if (!this.isInitialized) return;

        try {
            const userData = getUserAddDataFromLocalStorage();
            if (userData && userData.uid) {
                this.currentUser = userData;
                
                // Set user ID and properties for Firebase Analytics
                // Include all available user properties to match debug event behavior
                if (this.analytics) {
                    setUserId(this.analytics, userData.uid);
                    
                    setUserProperties(this.analytics, {
                        user_id: userData.uid,
                        user_group: userData.userGroup,
                        user_timezone: userData.timezone,
                        user_language: userData.language,
                        user_partner_types: userData.partnerTypes ? userData.partnerTypes.join(',') : '',
                        user_roles: userData.roles ? userData.roles.join(',') : ''
                    });
                    
                    console.log('Firebase Analytics user properties set:', {
                        user_id: userData.uid,
                        user_group: userData.userGroup,
                        user_timezone: userData.timezone,
                        user_language: userData.language,
                        user_partner_types: userData.partnerTypes ? userData.partnerTypes.join(',') : '',
                        user_roles: userData.roles ? userData.roles.join(',') : ''
                    });
                }
                
                // Also set for gtag if available (backward compatibility)
                if (this.gtag) {
                    this.gtag('config', import.meta.env.VITE_GOOGLE_ANALYTICS_ID, {
                        user_id: userData.uid,
                        custom_map: {
                            'custom_parameter_2': 'user_group',
                            'custom_parameter_3': 'user_roles',
                            'custom_parameter_6': 'user_timezone',
                            'custom_parameter_7': 'user_language',
                            'custom_parameter_8': 'user_partner_types'
                        }
                    });

                    this.gtag('set', {
                        user_id: userData.uid,
                        user_group: userData.userGroup,
                        user_timezone: userData.timezone,
                        user_language: userData.language,
                        user_partner_types: userData.partnerTypes ? userData.partnerTypes.join(',') : '',
                        user_roles: userData.roles ? userData.roles.join(',') : ''
                    });
                }

                console.log('User properties updated for analytics:', {
                    uid: userData.uid,
                    userGroup: userData.userGroup
                });
            }
        } catch (error) {
            console.error('Error updating user properties:', error);
        }
    }

    /**
     * Track page views with user context
     * @param {string} pageTitle - The page title
     * @param {string} safePath - The sanitized path/query (see buildSafePath)
     */
    trackPageView(pageTitle, safePath = '/') {
        if (!this.isInitialized) return;

        const userData = this.currentUser || getUserAddDataFromLocalStorage();
        const pageLocation = this.sanitizePageLocation(safePath);
        const pageUrl = new URL(pageLocation);
        
        const pageViewData = {
            page_path: `${pageUrl.pathname}${pageUrl.search}`,
            page_title: this.sanitizePrivateText(pageTitle),
            page_location: pageLocation,
            user_id: userData?.uid,
            user_group: userData?.userGroup,
            user_timezone: userData?.timezone,
            user_language: userData?.language,
            user_partner_types: userData?.partnerTypes ? userData.partnerTypes.join(',') : '',
            user_roles: userData?.roles ? userData.roles.join(',') : ''
        };
        
        // Send to Firebase Analytics (primary method for BigQuery export)
        if (this.analytics) {
            logEvent(this.analytics, 'page_view', pageViewData);
        }
        
        // Note: Removed gtag event sending to prevent duplicate page_view events
        // Firebase Analytics handles all tracking for BigQuery export

        console.log('Page view tracked:', {
            pageLocation: pageViewData.page_location,
            pageTitle: pageViewData.page_title,
            userId: userData?.uid
        });
    }

    /**
     * Track custom events with user context
     * @param {string} eventName - The event name
     * @param {Object} parameters - Additional event parameters
     */
    trackEvent(eventName, parameters = {}) {
        if (!this.isInitialized) return;

        const userData = this.currentUser || getUserAddDataFromLocalStorage();
        
        const eventData = {
            ...this.sanitizeAnalyticsValue(parameters),
            user_id: userData?.uid,
            user_group: userData?.userGroup,
            user_timezone: userData?.timezone,
            user_language: userData?.language,
            user_partner_types: userData?.partnerTypes ? userData.partnerTypes.join(',') : '',
            user_roles: userData?.roles ? userData.roles.join(',') : '',
            timestamp: new Date().toISOString()
        };

        // Send event to Firebase Analytics (primary method for BigQuery export)
        if (this.analytics) {
            logEvent(this.analytics, eventName, eventData);
        }
        
        // Note: Removed gtag event sending to prevent duplicate events
        // Firebase Analytics handles all tracking for BigQuery export

        console.log('Custom event tracked:', { eventName, eventData });
    }

    /**
     * Track user actions (e.g., add driver, update driver, etc.)
     * @param {string} action - The action performed
     * @param {string} category - The category of the action
     * @param {Object} details - Additional details about the action
     */
    trackUserAction(action, category, details = {}) {
        if (!this.isInitialized) return;

        const userData = this.currentUser || getUserAddDataFromLocalStorage();
        
        const eventName = 'user_action';
        const parameters = {
            action_name: action,
            action_category: category,
            user_id: userData?.uid,
            user_group: userData?.userGroup,
            user_timezone: userData?.timezone,
            user_language: userData?.language,
            user_partner_types: userData?.partnerTypes ? userData.partnerTypes.join(',') : '',
            user_roles: userData?.roles ? userData.roles.join(',') : '',
            ...details
        };

        this.trackEvent(eventName, parameters);
    }

    /**
     * Track form submissions
     * @param {string} formName - The name of the form
     * @param {Object} formData - The form data (sanitized)
     */
    trackFormSubmission(formName, formData = {}) {
        if (!this.isInitialized) return;

        const userData = this.currentUser || getUserAddDataFromLocalStorage();
        
        this.trackEvent('form_submit', {
            form_name: formName,
            form_data: JSON.stringify(this.sanitizeAnalyticsValue(formData)),
            user_id: userData?.uid,
            user_group: userData?.userGroup,
            user_timezone: userData?.timezone,
            user_language: userData?.language,
            user_partner_types: userData?.partnerTypes ? userData.partnerTypes.join(',') : '',
            user_roles: userData?.roles ? userData.roles.join(',') : ''
        });
    }

    /**
     * Track API calls
     * @param {string} endpoint - The API endpoint
     * @param {string} method - The HTTP method
     * @param {number} statusCode - The response status code
     * @param {number} responseTime - The response time in ms
     */
    trackApiCall(endpoint, method, statusCode, responseTime) {
        if (!this.isInitialized) return;

        const userData = this.currentUser || getUserAddDataFromLocalStorage();
        
        this.trackEvent('api_call', {
            api_endpoint: endpoint,
            http_method: method,
            status_code: statusCode,
            response_time: responseTime,
            user_id: userData?.uid,
            user_group: userData?.userGroup,
            user_timezone: userData?.timezone,
            user_language: userData?.language,
            user_partner_types: userData?.partnerTypes ? userData.partnerTypes.join(',') : '',
            user_roles: userData?.roles ? userData.roles.join(',') : ''
        });
    }

    /**
     * Track errors
     * @param {string} errorType - The type of error
     * @param {string} errorMessage - The error message
     * @param {string} location - Where the error occurred
     */
    trackError(errorType, errorMessage, location) {
        if (!this.isInitialized) return;

        const userData = this.currentUser || getUserAddDataFromLocalStorage();
        
        this.trackEvent('error_occurred', {
            error_type: errorType,
            error_message: errorMessage,
            error_location: location,
            user_id: userData?.uid,
            user_group: userData?.userGroup,
            user_timezone: userData?.timezone,
            user_language: userData?.language,
            user_partner_types: userData?.partnerTypes ? userData.partnerTypes.join(',') : '',
            user_roles: userData?.roles ? userData.roles.join(',') : ''
        });
    }

    /**
     * Track login events
     * @param {string} method - The login method used
     */
    trackLogin(method = 'email') {
        const userData = this.currentUser || getUserAddDataFromLocalStorage();
        
        this.trackEvent('login', {
            method: method,
            user_id: userData?.uid || 'anonymous',
            user_group: userData?.userGroup || 'unknown',
            user_timezone: userData?.timezone || '',
            user_language: userData?.language || '',
            user_partner_types: userData?.partnerTypes ? userData.partnerTypes.join(',') : '',
            user_roles: userData?.roles ? userData.roles.join(',') : ''
        });
        
        // Update user properties after login
        setTimeout(() => {
            this.updateUserProperties();
        }, 1000);
    }

    /**
     * Track logout events
     */
    trackLogout() {
        this.trackEvent('logout', {});
        this.currentUser = null;
    }

    /**
     * Track search events
     * @param {string} searchTerm - The search term
     * @param {string} searchCategory - The category being searched
     * @param {number} resultsCount - Number of results returned
     */
    trackSearch(searchTerm, searchCategory, resultsCount) {
        this.trackEvent('search', {
            search_term: searchTerm,
            search_category: searchCategory,
            results_count: resultsCount
        });
    }

    /**
     * Track file downloads
     * @param {string} fileName - The name of the downloaded file
     * @param {string} fileType - The type of file
     */
    trackDownload(fileName, fileType) {
        this.trackEvent('file_download', {
            file_name: fileName,
            file_type: fileType
        });
    }

    /**
     * Refresh user properties (clear cache and update)
     * Useful after user data changes
     */
    refreshUserProperties() {
        this.currentUser = null; // Clear cached user data
        this.updateUserProperties(); // Fetch fresh data and update properties
    }
}

// Create and export a singleton instance
const analyticsService = new AnalyticsService();
export default analyticsService;

// Export specific tracking functions for convenience
export const {
    trackPageView,
    trackEvent,
    trackUserAction,
    trackFormSubmission,
    trackApiCall,
    trackError,
    trackLogin,
    trackLogout,
    trackSearch,
    trackDownload,
    updateUserProperties,
    refreshUserProperties
} = analyticsService;